import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { modelOf } from '../../../prop-types';
import PropTypes from 'prop-types';

import LanguageStore from '../../../store/LanguageStore';
import CurrencyStore from '../../../store/CurrencyStore';
import CountryStore from '../../../store/CountryStore';
import AccountStore from '../../../store/AccountStore';

@observer
export class RegionalSettings extends Component {
  constructor(props) {
    super(props);

    this.saveRequestId = 0;
    this.state = this.createInitialState(props);
  }

  componentWillUnmount() {
    if (!this.state.isSaving) {
      this.saveRequestId += 1;
    }
  }

  createInitialState = (props = this.props) => {
    const { languageStore, currencyStore, countryStore } = props;

    return {
      country: countryStore.activeCountry,
      language: languageStore.activeLanguage,
      currency: currencyStore.activeCurrency,
      effectiveCurrency: currencyStore.activeCurrency,
      isSaving: false,
      hasError: false,
      error: null,
    };
  };

  getSelectionState = (state = this.state) => ({
    country: state.country,
    language: state.language,
    currency: state.currency,
    effectiveCurrency: state.effectiveCurrency,
  });

  getNextSelectionState = (field, value, state = this.state) => {
    const nextState = this.getSelectionState(state);

    switch (field) {
      case 'language':
        nextState.language = value;
        break;
      case 'country':
        nextState.country = value;
        nextState.effectiveCurrency = value.currency || nextState.currency;
        break;
      case 'currency':
        nextState.currency = value;
        nextState.effectiveCurrency = value;
        break;
      default:
        break;
    }

    return nextState;
  };

  saveSelection = (selectionState) => {
    const { languageStore, accountStore } = this.props;
    const requestId = ++this.saveRequestId;

    this.setState(() => ({
      isSaving: true,
      hasError: false,
      error: null,
    }));

    return languageStore
      .getRedirectedUrl(selectionState.language.code)
      .then((url) => {
        if (requestId !== this.saveRequestId) {
          return false;
        }

        return accountStore
          .updateSessionParams({
            currency: selectionState.effectiveCurrency.code,
            country: selectionState.country.id,
            lang: selectionState.language.code,
          })
          .then(() => {
            if (window.location.href !== url.href) {
              window.location = url;
            } else {
              window.location.reload();
            }

            return true;
          });
      })
      .catch((error) => {
        if (requestId !== this.saveRequestId) {
          return false;
        }

        this.setState({
          isSaving: false,
          hasError: true,
          error,
        });

        return false;
      });
  };

  save = () => {
    return this.saveSelection(this.getSelectionState());
  };

  commitSelection = (field, value) => {
    if (this.state.isSaving) {
      return Promise.resolve(false);
    }

    return this.saveSelection(this.getNextSelectionState(field, value));
  };

  resetSelectionState = () => {
    this.saveRequestId += 1;
    this.setState(this.createInitialState());
  };

  setLanguage = (language) => {
    this.setState({
      language,
    });
  };

  setCountry = (country) => {
    let effectiveCurrency = this.state.currency;
    if (country.currency) {
      effectiveCurrency = country.currency;
    }

    this.setState({
      country,
      effectiveCurrency,
    });
  };

  setCurrency = (currency) => {
    this.setState({
      currency,
      effectiveCurrency: currency,
    });
  };

  onChange = (field, value) => {
    switch (field) {
      case 'language':
        this.setLanguage(value);
        break;
      case 'country':
        this.setCountry(value);
        break;
      case 'currency':
        this.setCurrency(value);
        break;
      default:
        break;
    }
  };

  render() {
    const { isSaving } = this.state;

    const effectiveState = {
      country: this.state.country,
      language: this.state.language,
      currency: this.state.effectiveCurrency,
    };
    const errorState = {
      hasError: this.state.hasError,
      error: this.state.error,
    };

    return (
      <Fragment>
        {this.props.children(
          this.save,
          this.onChange,
          effectiveState,
          isSaving,
          errorState,
          this.commitSelection,
          this.resetSelectionState
        )}
      </Fragment>
    );
  }
}

RegionalSettings.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  languageStore: modelOf(LanguageStore).isRequired,
  currencyStore: modelOf(CurrencyStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
  children: PropTypes.func.isRequired,
};

export default inject(
  'accountStore',
  'countryStore',
  'currencyStore',
  'languageStore'
)(RegionalSettings);
